/**
 * External dependencies.
 */
import type { GetStaticProps, NextPage } from "next";

/**
 * Internal dependencies.
 */
import {
  fetchAPI,
  getLocale,
  getLocalizationData,
  loadTranslation,
} from "utils";
import Layout from "../../layout/layout";
import TealBox from "components/teal-box/teal-box";
import FormTopicSearch from "blocks/form-topic-search/form-topic-search";
import FormContact from "blocks/form-contact/form-contact";
import QuickHelp from "components/quick-help/quick-help";
import SchemaSet from "components/schema-set/schema-set";
import Breadcrumbs from "blocks/breadcrumbs/breadcrumbs";
import Markdown from "components/markdown/markdown";
import SEO from "components/seo/seo";

const Contact: NextPage = ({ data, locale }: any) => {
  const { SearchTitle, FormSection, QuickLinksBox, QuickLinksTitle } = data;

  return (
    <Layout title="Contact us" desc="Contact us" light notice>
      <SEO data={data.SEO} />
      <SchemaSet
        schemaData={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: FormSection?.Title || "",
          text: FormSection?.Content || "",
        }}
      />
      <TealBox center maxWidth="544px">
        <h1 className="h3 ta-c">{SearchTitle}</h1>
        <FormTopicSearch locale={locale} />
      </TealBox>
      <div className="container container--small">
        <Breadcrumbs />
        <div className="contact-row">
          <div className="contact-row__left">
            <h4>{FormSection?.Title}</h4>
            <Markdown content={FormSection?.Content} />
            <FormContact data={FormSection} />
          </div>
          <div className="contact-row__right">
            <QuickHelp items={QuickLinksBox} title={QuickLinksTitle} />
          </div>
        </div>
      </div>
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  const translation = await loadTranslation(
    ctx.locale!,
    process.env.NODE_ENV === "production"
  );
  const locale = getLocale(ctx.locale);

  const { data } = await fetchAPI("/contact", {
    populate: {
      SEO: {
        populate: "*",
      },
      FormSection: {
        populate: "*",
      },
      QuickLinksBox: {
        populate: "*",
      },
      localizations: "*",
    },
    locale,
  });

  return {
    props: {
      translation,
      data: data?.attributes || {},
      locale: ctx.locale,
      localizationData: getLocalizationData(data, "contact-us"),
    },
    revalidate: 10,
  };
};

export default Contact;
